import { isEmpty as _isEmpty, find as _find } from "lodash-es";
import PropTypes from "prop-types";
import React, { useCallback, useState, useMemo } from "react";
import { connect } from "react-redux";
import dayjs from "../utils/dayjs";
import tw from "tailwind-styled-components";
import styled from "styled-components";
import { Link } from "react-router-dom";

import Loading from "../components/Loading";
import Button from "../components/Button";
import { createLoadingState } from "../utils/selectors";
import Avatar from "../components/Avatar";
import { getHumanUrl } from "../utils";
import Markup from "../components/Markup";
import Input from "../components/Input";

const TimelineWrapper = tw.div`
bg-gray-100 
min-w-0 
w-full
lg:flex-1
pb-16
lg:pb-0
`;

const YearSelect = tw(Input)`
min-w-0
m-1
h-10
`;

const TimelineListWrapper = tw.div`
lg:py-7
lg:pl-4
p-0.5
`;

const NoEventsBanner = tw.div`
rounded-md 
bg-sky-100 
p-4 
text-sky-700
`;

const TimeLineItemStyled = styled.div`
  min-height: 85px;
`;

const TimelineItem = tw(TimeLineItemStyled)`
flex space-x-3
`;
const getLoadingState = createLoadingState(["events"]);

const mapStateToProps = (state) => ({
  ...getLoadingState(state),
  events: state.events,
  humans: state.humans,
});

function TimelineRow({ event, shouldShowBorder }) {
  const generateHumanUrl = useMemo(() => {
    return event.human ? getHumanUrl(event.human) : `/h/${event.humanId}`;
  }, [event.human, event.humanId]);

  return (
    <li>
      <TimelineItem>
        <div className="flex flex-col">
          <Link to={generateHumanUrl}>
            <Avatar human={event.human} size="45px" />
          </Link>
          <div className="flex relative justify-center h-full">
            {shouldShowBorder ? (
              <div
                className="absolute h-full w-1 bg-gray-300"
                aria-hidden="true"
              />
            ) : null}
          </div>
        </div>
        <div className="min-w-0 flex-1 pt-0.5 flex justify-between space-x-4">
          <div>
            <p className="text-sm text-gray-500">
              <Link
                to={generateHumanUrl}
                className="text-gray-900 font-bold pr-1 hover:underline"
              >
                {event.human.name}
              </Link>{" "}
              <time className="text-gray-900">
                {dayjs(event.date).format("MMM Do, YYYY")}
              </time>
            </p>
            <div className="text-sm text-gray-500">
              {event.label.split("\n").map((label, key) => (
                <p key={key}>
                  <Markup>{label}</Markup>
                </p>
              ))}
            </div>
          </div>
        </div>
      </TimelineItem>
    </li>
  );
}

TimelineRow.propTypes = {
  event: PropTypes.object.isRequired,
  shouldShowBorder: PropTypes.bool.isRequired,
};

const Timeline = ({ loadingEvents, events, humans }) => {
  const [eventsFilter, setEventsFilter] = useState(() => {
    const now = new Date();
    return {
      year: now.getFullYear(),
    };
  });

  const visibleEvents = useMemo(() => {
    const visibleEvents = [];

    for (let e of events) {
      const date = new Date(e.date);
      const neededYear = date.getFullYear() === eventsFilter.year;
      if (!neededYear) continue;
      visibleEvents.push({
        ...e,
        _type: "event",
        human: _find(humans, { id: e.humanId }),
      });
    }

    visibleEvents.sort((a, b) => new Date(a.date) - new Date(b.date));

    return visibleEvents;
  }, [events, humans, eventsFilter]);

  const onChange = useCallback(
    (event) => {
      const target = event.target;
      const value = target.type === "checkbox" ? target.checked : target.value;
      const name = target.name;

      setEventsFilter({ ...eventsFilter, [name]: parseInt(value, 10) });
    },
    [eventsFilter]
  );

  const onPrevNext = useCallback(
    (event) => {
      const next = event.target.name === "next";

      const filter = { ...eventsFilter };

      filter.year += next ? 1 : -1;

      setEventsFilter(filter);
    },
    [eventsFilter]
  );

  if (loadingEvents) {
    return <Loading />;
  }

  return (
    <TimelineWrapper>
      <div className="flex gap-1 justify-end items-center mt-3 mb-5 lg:mb-0">
        <YearSelect
          type="number"
          name="year"
          id="year"
          value={eventsFilter.year}
          onChange={onChange}
        />
        <Button className="h-10" name="prev" onClick={onPrevNext}>
          Prev
        </Button>
        <Button className="h-10" name="next" onClick={onPrevNext}>
          Next
        </Button>
      </div>

      <TimelineListWrapper>
        {_isEmpty(visibleEvents) ? (
          <NoEventsBanner>No events for this year</NoEventsBanner>
        ) : (
          <ul>
            {visibleEvents.map((event, eventIndex) => (
              <TimelineRow
                event={event}
                key={event.id}
                shouldShowBorder={eventIndex !== visibleEvents.length - 1}
              />
            ))}
          </ul>
        )}
      </TimelineListWrapper>
    </TimelineWrapper>
  );
};

Timeline.propTypes = {
  loadingEvents: PropTypes.bool.isRequired,
  events: PropTypes.arrayOf(PropTypes.object).isRequired,
  humans: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default connect(mapStateToProps)(Timeline);
